import React, { useEffect, useMemo, useState } from "react";
import style from "./style";
import {
  Box,
  Grid,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ReactComponent as Edit } from "../../assets/images/edit.svg";
import Modal from "../../components/Modal";
import dayjs from "dayjs";
import { getDateFormat } from "../../utils/dateFormatStore";

const ThresholdPage = ({
  data = [],
  taxCountry,
  taxPeriod = [],
  year,
  loading,
  handleAddThreshold,
  handleEditThreshold,
  handleDeleteThreshold,
  getYearThresholds,
}) => {
  const [open, setOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedThreshold, setSelectedThreshold] = useState(null);
  const [selectedTaxYear, setSelectedTaxYear] = useState("");
  const [yearId, setYearId] = useState("");

  const [ageFrom, setAgeFrom] = useState("");
  const [ageTo, setAgeTo] = useState("");
  const [thresholdAmount, setThresholdAmount] = useState("");
  const [originalValues, setOriginalValues] = useState(null);
  const role = localStorage.getItem("role");
  const numericInputPattern = /^\d*\.?\d*$/;

  const handleNumericInput = (setter) => (event) => {
    const { value } = event.target;
    if (value === "" || numericInputPattern.test(value)) {
      setter(value);
    }
  };

  const countryPermissions = JSON.parse(
    localStorage.getItem("countryPermissions") || "{}"
  );

  const permissions = Object.values(countryPermissions)[0] || {};

  const canEditThreshold = role === "SuperAdmin" ||
    permissions?.threshold?.edit === true;

  useEffect(() => {
    if (year) {
      setSelectedTaxYear(year);
      setYearId(year);
    }
  }, [year]);

  useEffect(() => {
    if (!open) {
      setAgeFrom("");
      setAgeTo("");
      setThresholdAmount("");
      setSelectedThreshold(null);
      setEditMode(false);
      setOriginalValues(null);
    }
  }, [open]);

  const handleTaxYearChange = async (event) => {
    setSelectedTaxYear(event.target.value);
    setYearId(event.target.value);
    if (getYearThresholds) {
      await getYearThresholds(event.target.value, taxCountry);
    }
  };

  const handleOpenEditModal = (item) => {
    setAgeFrom(item.ageFrom?.toString() || "");
    setAgeTo(item.ageTo?.toString() || "");
    setThresholdAmount(item.thresholdValue?.toString() || "");
    setSelectedThreshold(item._id || null);
    setEditMode(true);
    setOpen(true);
    setOriginalValues({
      ageFrom: item.ageFrom?.toString() || "",
      ageTo: item.ageTo?.toString() || "",
      thresholdAmount: item.thresholdValue?.toString() || "",
    });
  };

  const parseNumber = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const getAgeFromError = () => {
    if (ageFrom === "") return "";
    const val = parseNumber(ageFrom);
    if (val === null || val < 0) return "Age From must be 0 or greater";
    if (!Number.isInteger(val)) return "Age must be a whole number";
    if (val > 150) return "Age cannot exceed 150";
    return "";
  };

  const getAgeToError = () => {
    if (ageTo === "") return "";
    const val = parseNumber(ageTo);
    const fromVal = parseNumber(ageFrom);
    if (val === null || val < 0) return "Age To must be 0 or greater";
    if (!Number.isInteger(val)) return "Age must be a whole number";
    if (val > 150) return "Age cannot exceed 150";
    if (fromVal !== null && val <= fromVal) return "Age To must be greater than Age From";
    return "";
  };

  const getAmountError = () => {
    if (thresholdAmount === "") return "";
    const val = parseNumber(thresholdAmount);
    if (val === null || val < 0) return "Amount must be 0 or greater";
    return "";
  };

  const isFormValid = () => {
    const ageFromValue = parseNumber(ageFrom);
    const ageToValue = parseNumber(ageTo);
    const amountValue = parseNumber(thresholdAmount);

    if (ageFromValue === null || ageFromValue < 0) return false;
    if (!Number.isInteger(ageFromValue)) return false;
    if (ageFromValue > 150) return false;
    if (ageToValue === null || ageToValue < 0) return false;
    if (!Number.isInteger(ageToValue)) return false;
    if (ageToValue > 150) return false;
    if (ageToValue <= ageFromValue) return false;
    if (amountValue === null || amountValue < 0) return false;

    return true;
  };

  const hasChanges = useMemo(() => {
    if (!editMode || !originalValues) return true;
    return !(
      ageFrom === originalValues.ageFrom &&
      ageTo === originalValues.ageTo &&
      thresholdAmount === originalValues.thresholdAmount
    );
  }, [editMode, originalValues, ageFrom, ageTo, thresholdAmount]);

  const handleSave = () => {
    if (!isFormValid()) return;

    const ageFromValue = parseNumber(ageFrom);
    const ageToValue = parseNumber(ageTo);
    const amountValue = parseNumber(thresholdAmount);

    if (editMode) {
      if (!hasChanges) return;
      if (handleEditThreshold) {
        handleEditThreshold(selectedThreshold, ageFromValue, ageToValue, amountValue, yearId);
      }
    } else {
      if (handleAddThreshold) {
        handleAddThreshold(ageFromValue, ageToValue, amountValue, taxCountry, yearId);
      }
    }
    setOpen(false);
  };

  const handleDeleteItem = () => {
    if (handleDeleteThreshold) {
      handleDeleteThreshold(selectedThreshold);
    }
    setOpenDelete(false);
  };

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => (a.ageFrom ?? 0) - (b.ageFrom ?? 0));
  }, [data]);

  const getAgeGroupLabel = (item, index) => {
    if (item.ageFrom !== undefined && item.ageTo !== undefined) {
      const isFirst = index === 0 && item.ageFrom === 0;
      const isLast = index === sortedData.length - 1;
      if (isFirst) return `Below ${item.ageTo + 1}`;
      if (isLast) return `${item.ageFrom}+`;
      return `${item.ageFrom} - ${item.ageTo}`;
    }
    if (item.ageGroup) return item.ageGroup;
    return "-";
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2, mt: 3, ml: 3, mr: 2 }}>
        <Typography sx={style.gridText}>Tax Thresholds</Typography>
      </Box>
      <Grid container sx={style.mainContainer}>
        <Grid item xs={12} lg={12} sx={style.gridbox}>
          <Box sx={style.modalGrid}>
            <Box sx={style.heading}>
              <Typography sx={style.head}>{taxCountry}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2 }}>
              {canEditThreshold && (
                <Button
                  variant="contained"
                  type="submit"
                  color="success"
                  sx={{ ...style.jobButtons, height: "2.8em" }}
                  onClick={() => {
                    setAgeFrom("");
                    setAgeTo("");
                    setThresholdAmount("");
                    setSelectedThreshold(null);
                    setEditMode(false);
                    setOpen(true);
                  }}
                >
                  + Add Threshold
                </Button>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, height: "2.8em" }}>
                <Typography sx={{ fontSize: "15px", fontWeight: "600", whiteSpace: "nowrap", display: "flex", alignItems: "center", height: "100%" }}>FY</Typography>
                <FormControl sx={{
                  minWidth: "200px",
                  height: "3.5em",
                  "& .MuiOutlinedInput-root": {
                    height: "2.8em",
                    borderRadius: "10px"
                  }
                }}>
                  <Select
                    value={selectedTaxYear || ""}
                    onChange={handleTaxYearChange}
                    name="taxyear"
                    sx={style.dropdown}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: '#9e9e9e' }}>Select FY</span>;
                      }
                      const selectedPeriod = taxPeriod?.find(item => item._id === selected);
                      return selectedPeriod
                        ? `${dayjs(selectedPeriod.start).format(getDateFormat())} - ${dayjs(selectedPeriod.end).format(getDateFormat())}`
                        : "Select FY";
                    }}
                  >
                    <MenuItem value="" disabled>Select FY</MenuItem>
                    {taxPeriod?.map((item) => (
                      <MenuItem key={item._id} sx={style.dropDownMenu} value={item._id}>
                        {dayjs(item.start).format(getDateFormat())} - {dayjs(item.end).format(getDateFormat())}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
          <TableContainer sx={style.noShadow}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={style.tableHead}>Age Group</TableCell>
                  <TableCell sx={style.tableHead}>Threshold Amount</TableCell>
                  {canEditThreshold && (
                    <TableCell sx={style.tableHead}>Action</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={canEditThreshold ? 3 : 2}
                      align="center"
                    >

                      <Typography variant="h6">Preparing data...</Typography>
                    </TableCell>
                  </TableRow>
                ) : sortedData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={canEditThreshold ? 3 : 2}
                      align="center"
                    >

                      <Typography variant="h6">No Data</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedData.map((item, index) => (
                    <TableRow key={item._id || index}>
                      <TableCell sx={style.tableData}>
                        {getAgeGroupLabel(item, index)}
                      </TableCell>
                      <TableCell sx={style.tableData}>
                        {item.thresholdValue?.toLocaleString() ?? "-"}
                      </TableCell>
                      {canEditThreshold && (
                        <TableCell>
                          <Box sx={style.tableActiondata}>
                            <Edit
                              style={{ cursor: "pointer", margin: 3 }}
                              onClick={() => handleOpenEditModal(item)}
                            />

                            <DeleteOutlineIcon
                              sx={{ color: "#ff0000", cursor: "pointer" }}
                              onClick={() => {
                                setSelectedThreshold(item._id);
                                setOpenDelete(true);
                              }}
                            />
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <Modal
        open={open}
        setOpen={setOpen}
        disabled={editMode ? !isFormValid() || !hasChanges : !isFormValid()}
        btnText={editMode ? "Update Threshold" : "Add Threshold"}
        title={editMode ? "Edit Threshold" : "Add New Threshold"}
        handleCheck={handleSave}
      >
        <Box sx={style.modalGrid}>
          <Box sx={style.box2}>
            <InputLabel sx={style.label}>Age From</InputLabel>
            <TextField
              size="small"
              value={ageFrom}
              onChange={handleNumericInput(setAgeFrom)}
              sx={style.box2}
              type="number"
              inputProps={{ min: 0 }}
              placeholder={editMode ? "Update age from" : "Enter age from"}
              error={!!getAgeFromError()}
              helperText={getAgeFromError()}
            />
          </Box>
          <Box sx={style.box2}>
            <InputLabel sx={style.label}>Age To</InputLabel>
            <TextField
              size="small"
              value={ageTo}
              onChange={handleNumericInput(setAgeTo)}
              sx={style.box2}
              type="number"
              inputProps={{ min: 0 }}
              placeholder={editMode ? "Update age to" : "Enter age to"}
              error={!!getAgeToError()}
              helperText={getAgeToError()}
            />
          </Box>
        </Box>
        <Box sx={{ ...style.modalGrid, mt: 2 }}>
          <Box sx={style.box2}>
            <InputLabel sx={style.label}>Threshold Amount</InputLabel>
            <TextField
              size="small"
              value={thresholdAmount}
              onChange={handleNumericInput(setThresholdAmount)}
              sx={style.box2}
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              placeholder={editMode ? "Update threshold amount" : "Enter threshold amount"}
              error={!!getAmountError()}
              helperText={getAmountError()}
            />
          </Box>
          <Box sx={style.box2} />
        </Box>
      </Modal>

      <Modal
        open={openDelete}
        setOpen={setOpenDelete}
        deletetText={"Delete"}
        cancelText={"Cancel"}
        title={"Delete Threshold"}
        handleDelete={handleDeleteItem}
      >
        <Box>
          <Typography>Are you sure you want to Delete?</Typography>
        </Box>
      </Modal>
    </>
  );
};

export default ThresholdPage;
